import React, { useCallback, useContext, useMemo, useState } from 'react';
import {
  Col,
  DatePicker,
  Input,
  Row,
  Button,
  Form,
  Upload,
  message,
  Skeleton,
  InputNumber,
  Select,
} from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { EventContext } from '.';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import { bytesToMegaBytes } from 'components/Form/utils';
import { getFileChecksum } from 'components/Form/ImagePicker';
import { getCountries, presignUrl, updateTournament, uploadToS3 } from 'api/events';
import Flex from 'componentsv2/shared-components/Flex';
import Avatar from 'antd/lib/avatar/avatar';
import CoordsField from 'components/Form/CoordsField';
import apiBase from 'api/base';
import { useQuery } from 'react-query';

const Information = () => {
  const { TextArea } = Input;

  const { eventId, event, reloadEvent } = useContext(EventContext);

  const [isloadingImageChange, setIsLoadingImageChange] = useState(false);

  const fetchCountries = useCallback(async () => {
    const { body } = await getCountries();

    return body?.data || [];
  }, []);

  const { data } = useQuery('countries', fetchCountries);

  const countries = useMemo(() => {
    if (Array.isArray(data)) {
      return data.map(country => {
        return {
          value: country.id,
          label: country.name,
        };
      });
    }
  }, [data]);

  const access_token = useSelector(state => state?.session?.access_token);

  const disabledDate = current => {
    // Can not select days before today and today
    return current && current < moment().endOf('day');
  };

  const disabledLastDate = current => {
    // Can not select days before today and today
    return current && current > moment(event?.start_date).endOf('day');
  };

  const formatValuesWithImage = image => {
    return {
      avatar: image,
      name: event.name,
      registration_cost: String(event?.registration_cost),
      minimum_members: Number(event?.minimum_members),
      description: event?.description,
      start_date: event?.start_date,
      last_registration_date: event?.last_registrtion_date,
      last_lineup_registration_at: event?.last_lineup_registration_at,
      place: event?.place,
      lat: Number(event?.lat),
      lng: Number(event?.lng),
      country_id: event?.country?.id,
    };
  };

  const formatValues = ({ country_id, coordinates, ...values }) => {
    return {
      ...values,
      lat: coordinates?.lat,
      lng: coordinates?.lng,
      country_id: Number(country_id),
    };
  };

  const onSubmitForm = async values => {
    try {
      await updateTournament(eventId, formatValues(values));
      reloadEvent();
      message.success('!El evento fue actualizado!');
    } catch (error) {
      message.error('!Ha ocurrido un error!');
    }
  };

  const onSubmitImageUpdate = async image => {
    try {
      await updateTournament(eventId, formatValuesWithImage(image));
      reloadEvent();
      setIsLoadingImageChange(false);
      message.success('!La imagen fue actualizada!');
    } catch (error) {
      message.error('!Ha ocurrido un error!');
      setIsLoadingImageChange(false);
    }
  };

  const onFileChange = useCallback(
    file => {
      setIsLoadingImageChange(true);

      if (bytesToMegaBytes(file.size) > 1) {
        message.error('El archivo debe pesar 1MB como máximo.');
        return null;
      }

      getFileChecksum(file).then(checksum => {
        const image = {
          byte_size: file.size,
          filename: file.name,
          content_type: file.type,
          checksum,
        };

        try {
          presignUrl(image).then(({ status, body }) => {
            if (status !== 200) {
              throw new Error('Presign Failed');
            }

            // Amazon requires the request to not have authorization token
            apiBase.jwt();
            uploadToS3(body, file).then(({ status: uploadStatus }) => {
              // restore token
              apiBase.jwt(access_token);

              if (uploadStatus !== 201) {
                throw new Error('Upload Failed');
              }

              // form.change('avatar', body?.blob_signed_id);
              onSubmitImageUpdate(body?.blob_signed_id);
            });
          });
        } catch (_) {
          // restore token
          apiBase.jwt(access_token);
          message.error(
            'El archivo seleccionado no puede ser procesado. Porfavor intenta nuevamente. Si el error persiste recomendamos que seleccione otro archivo.',
          );
        }
      });
    },
    [access_token],
  );

  return (
    <>
      <Flex alignItems="center" mobileFlex={false} className="text-center text-md-left">
        {isloadingImageChange && <Skeleton.Avatar size={90} active />}
        {!isloadingImageChange && (
          <Avatar src={event.avatar_url} size={90} icon={<UserOutlined />} />
        )}
        <div className="ml-md-3 mt-md-0 mt-3">
          <Upload showUploadList={false} customRequest={({ file }) => onFileChange(file)}>
            <Button type="primary">Cambiar imagen</Button>
          </Upload>
        </div>
      </Flex>
      <div className="mt-4">
        <Form
          onFinish={values => onSubmitForm(values)}
          name="basicInformation"
          layout="vertical"
          initialValues={{
            name: event.name,
            country_id: event?.country?.id,
            registration_cost: event?.registration_cost,
            minimum_members: event?.minimum_members,
            description: event?.description,
            start_date: moment(event?.start_date, 'YYYY-MM-DD HH:mm'),
            last_registration_date: moment(event?.last_registration_date, 'YYYY-MM-DD'),
            last_lineup_registration_at: moment(event?.last_lineup_registration_at, 'YYYY-MM-DD'),
            place: event?.place,
            coordinates: { lat: Number(event?.lat), lng: Number(event?.lng) },
          }}
        >
          <Row>
            <Col xs={24} sm={24} md={24} lg={16}>
              <Row gutter={ROW_GUTTER}>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    label="Nombre del evento"
                    name="name"
                    rules={[
                      {
                        required: true,
                        message: 'Ingresa el nombre del torneo!',
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    label="País"
                    name="country_id"
                    rules={[
                      {
                        required: true,
                        message: 'Ingresa el país!',
                      },
                    ]}
                  >
                    <Select options={countries} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    label="Costo de Inscripción ($)"
                    name="registration_cost"
                    rules={[
                      {
                        required: true,
                        message: 'Ingresa el costo de inscripción!',
                      },
                    ]}
                  >
                    <InputNumber precision={2} min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    label="Mínimo de Integrantes"
                    name="minimum_members"
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese el numero de integrantes!',
                      },
                    ]}
                  >
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={24}>
                  <Form.Item
                    label="Descripción"
                    name="description"
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese la descripción!',
                      },
                    ]}
                  >
                    <TextArea />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    label="Fecha de Inicio"
                    name="start_date"
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese la fecha de inicio!',
                      },
                    ]}
                  >
                    <DatePicker
                      showTime={{ format: 'HH:mm' }}
                      format={'DD-MM-YYYY HH:mm'}
                      className="w-100"
                      disabledDate={disabledDate}
                      showNow={false}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    label="Último día de Inscripción"
                    name="last_registration_date"
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese el ultimo día de inscripción!',
                      },
                    ]}
                  >
                    <DatePicker
                      disabledDate={disabledLastDate}
                      showNow={false}
                      className="w-100"
                      format={'DD-MM-YYYY'}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    label="Fecha límite para inscribir jugadores"
                    name="last_lineup_registration_at"
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese la fecha límite para inscribir jugadores',
                      },
                    ]}
                  >
                    <DatePicker showNow={false} className="w-100" format={'DD-MM-YYYY'} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    label="Nombre del Lugar"
                    name="place"
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese el nombre del lugar!',
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={24}>
                  <Form.Item
                    label="Mapa"
                    name="coordinates"
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese la locación!',
                      },
                    ]}
                  >
                    <CoordsField />
                  </Form.Item>
                </Col>
              </Row>
              <Button type="primary" htmlType="submit">
                Guardar cambios
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    </>
  );
};

export default Information;
